// [목적] GET /festivals 축제 목록 조회 클라이언트 — fetchWithEnvelope + packages/types 공유 zod 스키마 + 날짜/주소 포맷 유틸
// [이유] fe-home-festivals-live: 홈 "강원 축제" 섹션이 mock(FESTIVALS 상수) 대신 실 API 를 사용해야 함.
//        BE 계약(2026-07-20 FE 병렬 구현 확정): GET /festivals?limit=12 → envelope { data: FestivalItem[] }.
//        정렬은 서버가 수행(ongoing→upcoming→unknown→ended) — FE 는 재정렬하지 않는다.
//        packages/types/src/festival.ts 에 BE/FE 공유 스키마가 이미 존재 — collection.client.ts/folioClient.ts
//        패턴과 동일하게 자체 미러 대신 공유 스키마를 import(CLAUDE §6-5: 응답은 zod 파싱 후 사용).
// [사이드 이팩트] 없음(조회 전용, GET). useFestivals 훅이 이 함수를 TanStack Query 로 래핑.
// [수정 시 고려사항] BE 스키마 변경 시 packages/types/src/festival.ts 가 SoT — 이 파일은 재export/재사용만 담당.
//                   날짜 결측 실데이터는 status='unknown'으로 내려오며 일정 문구 없이 배지만 표시한다.

import {
  festivalItemSchema,
  festivalListResponseSchema,
  type FestivalItem,
  type FestivalStatus,
} from '@gangwoncanvas/types';

import { fetchWithEnvelope } from './http.js';

export type Festival = FestivalItem;
export type { FestivalStatus };

// ── API 함수 ─────────────────────────────────────────────────────────────────

/**
 * [목적] GET /festivals — 강원 축제 목록 조회(공개, 서버 정렬)
 * [이유] 홈 "강원 축제" 섹션의 히어로(첫 항목) + 카드 3개(다음 항목들) 데이터 소스.
 * [사이드 이팩트] 없음(조회 전용).
 * [수정 시 고려사항] limit 기본값 12 — 히어로 1 + 카드 3 만 소비하지만 여유 있게 받아 후속 "전체 보기" 캐시 재사용 여지.
 */
export async function listFestivals(limit = 12): Promise<Festival[]> {
  const data = await fetchWithEnvelope<unknown>(`/festivals?limit=${limit}`);
  return festivalListResponseSchema.parse(data);
}

/**
 * [목적] GET /festivals/:id — 축제 단건 상세 조회(공개)
 * [이유] 상세 화면이 목록(limit 최대 36) find 우회를 쓰던 구조라, 목록 밖으로 밀린 축제는 직접 URL·새로고침·공유 링크로
 *        열리지 않았다. BE 단건 엔드포인트(slice 이전 전량 조회)를 직접 호출해 진입 경로와 무관하게 열리게 한다.
 * [사이드 이팩트] 없음(조회 전용). 404(미존재/비축제/형식 불일치)는 fetchWithEnvelope 가 ApiError(status 404)로 throw —
 *                 호출부(useFestival/FestivalDetailPage)가 status 로 분기. contentId 는 encodeURIComponent 로 경로 세그먼트 안전화.
 * [수정 시 고려사항] 응답 shape 는 목록 원소와 동일한 festivalItemSchema(packages/types SoT) — 필드 확장은 BE 스키마와 동시 변경.
 */
export async function getFestival(contentId: string): Promise<Festival> {
  const data = await fetchWithEnvelope<unknown>(`/festivals/${encodeURIComponent(contentId)}`);
  return festivalItemSchema.parse(data);
}

// ── 포맷 유틸 (순수 함수) ────────────────────────────────────────────────────────

/**
 * [목적] 'YYYYMMDD' 문자열 → { month, day } 파싱
 * [이유] BE startDate/endDate 포맷(TourAPI eventstartdate 원본 포맷)을 화면 표시용 'M.D' 로 변환하기 위한 1차 파싱.
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] 형식/범위 불일치 시 null 반환 — 호출부가 방어적으로 처리(빈 문자열 등)해야 함.
 */
export function parseYYYYMMDD(value: string): { month: number; day: number } | null {
  if (!/^\d{8}$/.test(value)) return null;
  const month = Number(value.slice(4, 6));
  const day = Number(value.slice(6, 8));
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { month, day };
}

/**
 * [목적] startDate/endDate('YYYYMMDD') → 'M.D — M.D' 기간 라벨
 * [이유] 히어로/카드에 표시할 축제 기간 텍스트 생성.
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] 파싱 실패 시 빈 문자열 반환 — 호출부가 빈 문자열이면 기간 줄 자체를 생략해야 함.
 */
export function formatFestivalPeriod(startDate: string, endDate: string): string {
  const start = parseYYYYMMDD(startDate);
  const end = parseYYYYMMDD(endDate);
  if (!start || !end) return '';
  return `${start.month}.${start.day} — ${end.month}.${end.day}`;
}

/**
 * [목적] 연도를 포함한 기간 라벨 — "2026. 1.9 — 1.25"
 * [이유] 2026-09-20 디자인 QA: 목록 카드는 연도를 붙여 쓰고 있었는데 상세 화면은 "1.9 — 1.25"
 *        처럼 연도를 빼고 있었다. 종료된 축제가 대부분인 화면에서 연도 없는 날짜는
 *        "올해인가 작년인가"를 판단할 수 없게 만든다. 동일 포맷을 두 화면이 공유하도록
 *        FestivalsPage 로컬 함수였던 것을 이곳으로 올렸다.
 * [사이드 이팩트] FestivalsPage 카드 / FestivalDetailPage 메타 칩이 모두 이 함수를 쓴다.
 * [수정 시 고려사항] startDate 가 YYYYMMDD 8자리가 아니면 연도 없는 기본 포맷으로 폴백한다.
 */
export function formatFestivalPeriodWithYear(startDate: string, endDate: string): string {
  const period = formatFestivalPeriod(startDate, endDate);
  return period && /^\d{8}$/.test(startDate) ? `${startDate.slice(0, 4)}. ${period}` : period;
}

/**
 * [목적] addr1 전체 주소 → 시·군 수준 축약(앞 2어절)
 * [이유] TourAPI addr1 은 상세 주소(도로명/번지)까지 포함 — 카드/히어로에는 축약된 위치만 표시.
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] addr1 이 null/빈 문자열이면 null 반환 — 호출부가 위치 줄을 생략해야 함.
 */
export function formatFestivalLocation(addr1: string | null): string | null {
  const trimmed = addr1?.trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/).slice(0, 2).join(' ');
}

/**
 * [목적] overview 1줄 절단 — 지정 길이 초과 시 말줄임표(…) 추가
 * [이유] 히어로 설명 줄은 overview 가 있을 때만 1줄 분량으로 표시.
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] overview 가 null/빈 문자열이면 null 반환 — 호출부가 설명 줄 자체를 생략해야 함.
 */
export function truncateOverview(overview: string | null, max = 70): string | null {
  const trimmed = overview?.trim();
  if (!trimmed) return null;
  return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max)}…`;
}
